import { ComponentType, LazyExoticComponent } from 'react';
import { TypedFormatMessageFn, TypedFormatMessageWithJsxFn } from '../../types/intl';
import { BtlApplication, BtlCompany, Individual } from 'api/btlApplicationForm/types';

export type BtlFormSectionParam = BtlApplicationFormPageEnum | string;

export enum BtlApplicationFormPageEnum {
  CASE_SUMMARY = 'CASE_SUMMARY',
  FINANCING_AND_PROPERTY = 'FINANCING_AND_PROPERTY',
  FINANCING = 'FINANCING',
  PROPERTY = 'PROPERTY',
  EXPENSES = 'EXPENSES',
  APPLICANTS = 'APPLICANTS',
  APPLICANTS_COMPANY = 'APPLICANTS_COMPANY',
  APPLICANTS_INDIVIDUAL = 'APPLICANTS_INDIVIDUAL',
  PRODUCT = 'PRODUCT',
  TOP_SLICING = 'TOP_SLICING',
  TOP_SLICING_HOUSEHOLD = 'TOP_SLICING_HOUSEHOLD',
  AFFORDABILITY_CHECK = 'AFFORDABILITY_CHECK',
  SUBMIT_DIP = 'SUBMIT_DIP',
  SOLICITORS = 'SOLICITORS',
  BANK_DETAILS = 'BANK_DETAILS',
  ADDITIONAL_INFORMATION = 'ADDITIONAL_INFORMATION',
  COMMUNICATION = 'COMMUNICATION',
  DOCUMENTS = 'DOCUMENTS',
  VALUATION = 'VALUATION',
  POST_OFFER = 'POST_OFFER',
}

export type BaseFormSchemaProps = {
  formatMessage: TypedFormatMessageFn;
  // TODO: probably won't be optional in the end
  isDipAccepted?: boolean;
  isDipAcceptedOrLater?: boolean;
  isFormEditable?: boolean;
};

// Have added this to allow for backwards compatability, but
// could refactor to always use the formatMessage function with
// TypedFormatMessageWithJsxFn type
export type BaseFormSchemaWithJsxMessagesProps = {
  formatMessage: TypedFormatMessageWithJsxFn;
  // TODO: probably won't be optional in the end
  isDipAccepted?: boolean;
  isFormEditable?: boolean;
};

export type BtlFormRoute = {
  FormPage?: LazyExoticComponent<ComponentType<any>> | ComponentType<any>;
  previousSection?: BtlFormSectionParam;
  previousLabel?: string;
  nextSection?: BtlFormSectionParam;
  showDisableTopSlicing?: boolean;
  nextLabel?: string;
  showStandaloneFormFooter?: boolean;
  isFormEditable?: boolean;
  currentSection?: BtlApplicationFormPageEnum;
};

// Define a generic type for missing fields
type MissingFields<T, P extends BtlApplicationFormPageEnum = BtlApplicationFormPageEnum> = {
  [K in P]: K extends BtlApplicationFormPageEnum.PRODUCT
    ? Array<{ missingFields: (keyof T)[]; isCurrentProductSuitable: boolean }>
    : Array<{ missingFields: (keyof T)[] }>;
};

// Define specific missing fields types for different sections
type MissingIndividualFields = Pick<MissingFields<Individual>, BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL>;
type MissingBtlCompanyFields = Pick<MissingFields<BtlCompany>, BtlApplicationFormPageEnum.APPLICANTS_COMPANY>;

// Omit specific pages from the general missing fields structure
type MissingBtlApplicationFields = Omit<
  MissingFields<BtlApplication>,
  BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL | BtlApplicationFormPageEnum.APPLICANTS_COMPANY
>;

export type MissingCaseFields = MissingIndividualFields & MissingBtlCompanyFields & MissingBtlApplicationFields;

// Base structure for section problem values
type SectionProblemBase = {
  missingFields: string[];
};

type SectionProblemValue<Key extends BtlApplicationFormPageEnum> = Key extends BtlApplicationFormPageEnum.PRODUCT
  ? SectionProblemBase & { isCurrentProductSuitable: boolean }
  : SectionProblemBase[];

export type SectionProblems = Record<BtlApplicationFormPageEnum, SectionProblemValue<BtlApplicationFormPageEnum>>;
